import api from './api';

// W6 corte 18.B (2026-05-11) — service rulesets estendido com:
//   - validateRuleset (dry-run shape check sem persistir)
//   - getRuleset (detalhe único pro detalhe view + diff futuro)
//   - getRulesetAdoption (poll de quantos clients reportaram a versão nova
//     pós-publish — observability do hot-reload propagation).
//
// Endpoints backend:
//   POST   /admin/rulesets               → publish (signed + activate opcional)
//   POST   /admin/rulesets/validate      → dry-run (Erro 65 já existia, reuse)
//   GET    /admin/rulesets               → list (cursor pagination)
//   GET    /admin/rulesets/{uuid}        → get detail
//   POST   /admin/rulesets/{uuid}/activate
//   GET    /admin/releases/ruleset-adoption?top_n=N  → adoption rows agg

export interface Ruleset {
  id: string;
  version: string;
  rules: unknown;
  rules_sha256: string;
  signature: string;
  is_active?: boolean;
  notes?: string;
  created_at?: string;
}

export interface ValidateRulesetInput {
  version: string;
  rules: unknown;
  notes?: string;
}

export interface ValidateRulesetResult {
  valid: boolean;
  errors?: string[];      // listing dos motivos do reject (quando valid=false)
}

export async function listRulesets(): Promise<{ data: Ruleset[]; next_cursor?: number }> {
  const resp = await api.get<{ data: Ruleset[]; next_cursor?: number }>('/admin/rulesets');
  return resp.data;
}

export async function getRuleset(uuid: string): Promise<Ruleset> {
  const resp = await api.get<Ruleset>(`/admin/rulesets/${uuid}`);
  return resp.data;
}

export async function validateRuleset(
  input: ValidateRulesetInput,
): Promise<ValidateRulesetResult> {
  // Backend retorna 200 com {valid: true} OU 400 com erros — capturamos
  // 400 e devolvemos como {valid: false, errors: [...]}.
  try {
    const resp = await api.post<ValidateRulesetResult>('/admin/rulesets/validate', input);
    // Defensive: aceita resp shape variado
    if (resp.data && typeof resp.data === 'object' && 'valid' in resp.data) {
      return resp.data;
    }
    return { valid: true };
  } catch (e) {
    const err = e as { response?: { data?: { error?: { message?: string; details?: unknown } } } };
    const detail = err?.response?.data?.error;
    if (detail?.message) {
      return { valid: false, errors: [detail.message] };
    }
    return { valid: false, errors: ['validate falhou — sem mensagem do backend'] };
  }
}

export async function publishRuleset(input: {
  version: string;
  rules: unknown;
  activate?: boolean;
  notes?: string;
}): Promise<Ruleset> {
  const resp = await api.post<Ruleset>('/admin/rulesets', input);
  return resp.data;
}

export async function activateRuleset(uuid: string): Promise<Ruleset> {
  const resp = await api.post<Ruleset>(`/admin/rulesets/${uuid}/activate`, {});
  return resp.data;
}

// ============================================================
// Adoption poll — W6 corte 18.C reuse (já existe endpoint GET
// /admin/releases/ruleset-adoption). 18.B usa pra mostrar
// quantos sessions reportaram a versão recém-publicada via heartbeat.
// ============================================================

export interface RulesetAdoptionRow {
  ruleset_version: string;
  is_active: boolean;
  sessions_24h: number;
  ruleset_uuid: string;
}

export interface RulesetAdoptionReport {
  rows: RulesetAdoptionRow[];
  total_sessions_24h: number;
  active_version: string;
}

export async function getRulesetAdoption(topN = 20): Promise<RulesetAdoptionReport> {
  const resp = await api.get<RulesetAdoptionReport>('/admin/releases/ruleset-adoption', {
    params: { top_n: topN },
  });
  return resp.data;
}
